<script setup>
/**
 * NewWhatsAppChatLauncher
 *
 * Floating action button that opens the "New WhatsApp Chat" flow
 * (`NewWhatsAppChatFlow.vue`). Mounted once at the dashboard root
 * (`routes/dashboard/Dashboard.vue`), alongside the other floating
 * launchers there (`CopilotLauncher`, `FloatingCallWidget`) - same fixed
 * positioning convention, offset higher so the two don't overlap.
 *
 * Only renders when the account has at least one WhatsApp inbox; there's
 * nothing useful this flow can do otherwise.
 *
 * Keyboard shortcut: Alt+W opens/closes it from anywhere in the dashboard
 * (registered for discoverability in
 * `components/widgets/modal/constants.js`'s SHORTCUT_KEYS list too).
 */
import { computed, ref } from 'vue';
import { useMapGetter } from 'dashboard/composables/store';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';

import Popover from 'dashboard/components-next/popover/Popover.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import ButtonGroup from 'dashboard/components-next/buttonGroup/ButtonGroup.vue';
import NewWhatsAppChatFlow from 'dashboard/components-next/NewWhatsAppChat/NewWhatsAppChatFlow.vue';
import { hasWhatsAppInbox } from 'dashboard/components-next/NewWhatsAppChat/helpers/newWhatsAppChatHelper';

const inboxesList = useMapGetter('inboxes/getInboxes');
const popoverRef = ref(null);

const showLauncher = computed(() => hasWhatsAppInbox(inboxesList.value));

useKeyboardEvents({
  'Alt+KeyW': {
    action: () => {
      if (showLauncher.value) popoverRef.value?.toggle();
    },
  },
});

const handleClose = () => {
  popoverRef.value?.hide();
};
</script>

<template>
  <div v-if="showLauncher" class="fixed bottom-20 ltr:right-4 rtl:left-4 z-50">
    <Popover ref="popoverRef" align="end" :show-content-border="false">
      <template #default="{ isOpen }">
        <ButtonGroup
          class="rounded-full bg-n-alpha-2 backdrop-blur-lg p-1 shadow hover:shadow-md"
        >
          <Button
            icon="i-lucide-message-circle-plus"
            no-animation
            data-testid="new-whatsapp-chat-launcher"
            class="!rounded-full !bg-n-solid-3 dark:!bg-n-alpha-2 !text-n-slate-12 text-xl transition-all duration-200 ease-out hover:brightness-110"
            :class="{ '!bg-n-alpha-2 dark:!bg-n-slate-9/30': isOpen }"
            lg
          />
        </ButtonGroup>
      </template>
      <template #content>
        <NewWhatsAppChatFlow @close="handleClose" />
      </template>
    </Popover>
  </div>
</template>
